import React, { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { Home, BookHeart, MessageCircleHeart, Wind, Gamepad2, HeartPulse, Heart, User } from "lucide-react";
import SosButton from "./SosButton";
import LanguageSwitcher from "./LanguageSwitcher";
import ChildProfileModal from "./ChildProfileModal";
import { useLang } from "../lib/i18n";

export default function Layout() {
  const { t } = useLang();
  const [profileOpen, setProfileOpen] = useState(false);
  const navItems = [
    { to: "/", label: t("nav.home"), icon: Home, testId: "nav-home", end: true },
    { to: "/tips", label: t("nav.tips"), icon: BookHeart, testId: "nav-tips" },
    { to: "/ask", label: t("nav.ask"), icon: MessageCircleHeart, testId: "nav-chat" },
    { to: "/breathe", label: t("nav.breathe"), icon: Wind, testId: "nav-breathe" },
    { to: "/quiz", label: t("nav.quiz"), icon: Gamepad2, testId: "nav-quiz" },
    { to: "/mood", label: t("nav.mood"), icon: HeartPulse, testId: "nav-mood" },
    { to: "/wins", label: t("nav.wins"), icon: Heart, testId: "nav-wins" },
  ];
  return (
    <div className="min-h-screen paper-bg flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FDFBF7]/85 border-b border-[#E8E6E1]" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <NavLink to="/" className="flex items-center gap-3 group" data-testid="brand-link">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#E07A5F] via-[#F2CC8F] to-[#81B29A] grid place-items-center text-white font-heading font-bold text-lg shadow-sm group-hover:scale-105 transition-transform">
              {t("brand.initial")}
            </div>
            <div className="hidden sm:block">
              <div className="font-heading font-semibold text-[#3D405B] leading-none">{t("brand.title")}</div>
              <div className="text-xs text-[#8A8D91]">{t("brand.tagline")}</div>
            </div>
          </NavLink>
          <div className="flex items-center gap-2">
            <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                      isActive ? "bg-[#3D405B] text-white" : "text-[#3D405B] hover:bg-[#F4F1DE]"
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <button
              onClick={() => setProfileOpen(true)}
              data-testid="profile-open"
              aria-label={t("profile.badge")}
              title={t("profile.badge")}
              className="w-9 h-9 rounded-full border border-[#E8E6E1] bg-white hover:bg-[#F4F1DE] text-[#3D405B] grid place-items-center transition-colors"
            >
              <User className="w-4 h-4" />
            </button>
            <LanguageSwitcher compact />
          </div>
        </div>
        <nav className="lg:hidden flex overflow-x-auto px-3 pb-3 gap-2" data-testid="mobile-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`m-${item.testId}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap font-medium transition-colors ${
                  isActive ? "bg-[#3D405B] text-white" : "text-[#3D405B] bg-white border border-[#E8E6E1]"
                }`
              }
            >
              <item.icon className="w-3.5 h-3.5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-5 md:px-8 py-8 md:py-12">
        <Outlet />
      </main>
      <footer className="border-t border-[#E8E6E1] mt-12 py-8 px-5 md:px-8" data-testid="app-footer">
        <div className="max-w-7xl mx-auto text-center text-sm text-[#8A8D91]">
          <p className="font-heading text-[#3D405B]">« {t("footer.quote")} »</p>
          <p className="mt-2">{t("brand.title")} — {t("footer.credit")}</p>
          <p className="mt-3 text-[11px]">
            <Link to="/presse" data-testid="footer-presse-link" className="hover:text-[#3D405B] underline-offset-2 hover:underline">
              {t("footer.press")}
            </Link>
          </p>
        </div>
      </footer>
      <SosButton />
      <ChildProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}
